import { useState, useEffect } from "react";
import axios from "axios";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { T, TONES, MANROPE } from "./clientsTokens";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Clients directive §22 — derived triage above the fold. The band itself is
   white; only the pills inside cells are tinted. */
export default function NeedsAttention({ clientId, refreshKey }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!clientId) return;
    let live = true;
    axios.get(`${API}/customer-success/clients/${encodeURIComponent(clientId)}/needs-attention`)
      .then((r) => { if (live) setData(r.data); })
      .catch(() => { if (live) setData(null); });
    return () => { live = false; };
  }, [clientId, refreshKey]);

  if (!data) return null;

  if (data.clear) {
    return (
      <div className="flex items-center gap-2 px-4 py-2.5 rounded-[10px]" data-testid="needs-attention-clear"
           style={{ background: T.sunken, border: `1px solid ${T.border}` }}>
        <CheckCircle2 className="w-3.5 h-3.5 shrink-0" style={{ color: TONES.success.dot }} />
        <span className="text-[12.5px]" style={{ color: T.ink2 }}>{data.summary}</span>
      </div>
    );
  }

  return (
    <div className="rounded-[10px] overflow-hidden" data-testid="needs-attention"
         style={{ background: T.card, border: `1px solid ${T.border}` }}>
      <div className="flex items-center gap-2 flex-wrap"
           style={{ background: T.sunken, borderBottom: `1px solid ${T.border}`, padding: "11px 16px" }}>
        <AlertTriangle className="w-3.5 h-3.5 shrink-0" style={{ color: "#C23A00" }} />
        <span style={{ ...MANROPE, fontSize: 14, fontWeight: 700, color: T.ink }}>Needs attention</span>
        <span className="text-[11.5px]" style={{ color: T.ink2 }} data-testid="needs-attention-summary">{data.summary}</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))" }}>
        {data.cells.map((c, i) => {
          const tone = c.pill ? (TONES[c.pill.tone] || TONES.neutral) : null;
          return (
            <div key={c.key} data-testid={`na-cell-${c.key}`}
                 style={{ padding: "14px 16px", borderLeft: i === 0 ? "none" : `1px solid ${T.dividerRow}`,
                          borderTop: `1px solid ${T.dividerRow}` }}>
              <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: "0.09em",
                            textTransform: "uppercase", color: T.muted }}>{c.label}</div>
              <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                <span className="text-[13.5px] font-semibold" style={{ color: T.ink }}>{c.value}</span>
                {c.pill && (
                  <span className="whitespace-nowrap" style={{ padding: "1px 8px", borderRadius: 999,
                        fontSize: 10.5, fontWeight: 700, background: tone.fill, color: tone.ink,
                        border: `1px solid ${tone.border}` }}>{c.pill.text}</span>
                )}
              </div>
              {(c.lines || []).map((l, j) => (
                <div key={j} className="text-[12.5px] mt-0.5 flex items-center gap-1.5"
                     style={{ color: T.ink2 }}>
                  {c.key === "unnamed_contacts" && (
                    <span className="w-[5px] h-[5px] rounded-full shrink-0" style={{ background: T.accentEdge }} />
                  )}
                  <span className="truncate">{l}</span>
                </div>
              ))}
              {c.link && (
                <a href={c.link} data-testid={`na-link-${c.key}`}
                   className="inline-block text-[11.5px] font-bold uppercase mt-1.5"
                   style={{ color: T.accentInk, letterSpacing: "0.04em" }}>{c.link_label || "Open"}</a>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
